<template>
  <component
    :is="MarketCard"
    :marketId="marketId"
    :marketIds="marketIds"
    :eventId="eventIdRef"
    :cardNumber="cardNumber"
    @click="onClick"
  />
</template>

<script lang="ts">
import {
  defineComponent,
  computed,
  PropType,
  ComputedRef,
  watchEffect
} from "vue";
import { Market } from "@fresh8/types/src/betData";
import {
  BetDataContext,
  injectBetData,
  provideEvent,
  provideEvents
} from "@fresh8/ad-data-composition/src/betData";
import {
  MOBILE_LEADERBOARD,
  LMLB,
  injectTemplateSize,
  TemplateSizeContext
} from "@fresh8/ad-data-composition/src/useTemplateSize";
import { CardTypes } from "@fresh8/types/src";
import { provideCardNumber, useAnalytics } from "../hooks/useAnalytics";
import Promo from "./markets/promo/WrapperSelector.vue";
import MainMarket from "./markets/multi-main/WrapperSelector.vue";
import Insight from "./markets/insight/WrapperSelector.vue";
import WDW from "./markets/win-draw-win/WrapperSelector.vue";
import WW from "./markets/win-win/WrapperSelector.vue";
import List from "./markets/list/WrapperSelector.vue";
import Parlay from "./markets/parlay/ParlayWrapper.vue";
import H2H from "./markets/h2h/WrapperSelector.vue";
import Offer from "./markets/offer/OfferWrapper.vue";
import CasinoHighlight from "./markets/casino/highlight/CasinoHighlightSelector.vue";
import CasinoList from "./markets/casino/list/CasinoListSelector.vue";

import { provideTeamColors } from "../lib/teamBranding";
import { providePromoOption } from "../hooks/usePromoOption";
import {
  injectAdTag,
  injectBrand
} from "@fresh8/ad-data-composition/src/brand";
import * as generateLinks from "@fresh8/ad-data-composition/src/lib/generateLinks";
import { injectRuntimeConfig } from "@fresh8/ad-data-composition/src/runtimeConfig";

/**
 * Checks if market display a draw option
 */

function hasDrawOption(eventMarket: Market) {
  return eventMarket.options.length > 2;
}

function getCard(cardType: CardTypes, eventMarket: Market, isMobile: boolean) {
  if (cardType === "mainMarket" && isMobile) {
    return hasDrawOption(eventMarket) ? CardMap["wdw"] : CardMap["ww"];
  }

  return CardMap[cardType];
}

const CardMap = {
  promo: Promo,
  mainMarket: MainMarket,
  insight: Insight,
  wdw: WDW,
  ww: WW,
  list: List,
  parlay: Parlay,
  h2h: H2H,
  offer: Offer,
  casinoHighlight: CasinoHighlight,
  casinoList: CasinoList
};

export default defineComponent({
  name: "CardSelector",
  props: {
    cardNumber: { type: String, required: false, default: "1" },
    eventId: { type: String, required: false, default: "" },
    marketIds: {
      type: Array as PropType<string[]>,
      required: true
    },
    optionIds: {
      type: Array as PropType<string[] | null>,
      required: true
    },

    cardType: { type: String as PropType<CardTypes>, required: true }
  },
  setup(props) {
    const adTag = injectAdTag();
    const rc = injectRuntimeConfig();
    const betData = injectBetData() || ({} as BetDataContext);
    const { handleClick: backgroundClick } = useAnalytics(
      { cardNumber: props.cardNumber },
      "Ad Background"
    );
    const { enableBackgroundClick } = injectBrand() || {};

    const { size } = injectTemplateSize() as TemplateSizeContext;

    const markets = computed(() => {
      return props.marketIds.map((id: string) => betData.markets.value?.[id]);
    }) as ComputedRef<Market[]>;

    const isMobile = computed(() =>
      [MOBILE_LEADERBOARD.name, LMLB.name].includes(size.value.name)
    );

    const MarketCard = computed(() => {
      return getCard(props.cardType, markets.value[0], isMobile.value);
    });

    const marketId = computed(() => markets.value[0]?.marketId);
    const eventIdRef = computed(() => markets.value[0]?.eventId);

    // Same logic we're using in /src/components/Wrapper.vue
    const handleBackgroundClick = computed(() => (e: Event) => {
      if (enableBackgroundClick?.value && rc.value.enableLinks) {
        backgroundClick();
        e.stopPropagation();
        let url = betData.links.value.registration;
        const campaignTarget = adTag?.selectedCampaign?.value?.targetUrl;
        const fallback =
          adTag?.adTag?.value?.tracking[
            adTag?.selectedOperator?.value?._id || ""
          ].fallbackUrl;
        if (campaignTarget && campaignTarget !== "") {
          url = generateLinks.generate(campaignTarget, {
            affiliateId: betData?.affiliateId.value,
            clickUrl: betData?.clickUrl.value,
            operatorKey: betData?.operator.value?.name || ""
          });
        } else if (fallback && fallback !== "") {
          url = generateLinks.generate(fallback, {
            affiliateId: betData?.affiliateId.value,
            clickUrl: betData?.clickUrl.value,
            operatorKey: betData?.operator.value?.name || ""
          });
        }

        window.open(url);
      }
    });

    provideEvent(eventIdRef.value);
    provideEvents(markets.value.map(m => m.eventId));
    provideTeamColors(eventIdRef.value);
    providePromoOption(props.cardType, props.optionIds);
    provideCardNumber(props.cardNumber);

    // ctx.expose({ eventId: eventId.value });

    return {
      MarketCard,
      marketId,
      onClick: handleBackgroundClick,
      eventIdRef
    };
  }
});
</script>
